import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ADD_TO_CART_EVENT, CLOSE_EVENT} from '@dcp/configurator';
import {DATA_LAYER_EVENT} from '@app/services/iframe/iframe-message.service';
import {EDDL_DATA_LAYER_NAME} from '@app/services/analytics/EDDL/dcp-eddl-collector.service';
import {withWipeBuffer} from '@app/services/analytics/EDDL/eddl-wipe-buffer.util';
import {EddlAdobeTrackingData} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {SafeResourceUrlPipe} from '../../pipes/safeResourceUrl/safeResourceUrl.pipe';

@Component({
  selector: 'dcp-iframe',
  templateUrl: './iframe.component.html',
  styleUrls: ['./iframe.component.scss'],
  imports: [SafeResourceUrlPipe],
})
export class IframeComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('iFrame', {static: false}) iframeComponent: ElementRef;
  @Input() title: string;
  @Input() show: boolean = false;
  @Input() url: string;

  @Output() closeEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  private messageHandler: (payload: any) => void = (payload) => {
    if (payload.data?.eventId === CLOSE_EVENT) {
      this.closeIframe();
    } else if (payload.data?.eventId === ADD_TO_CART_EVENT) {
      this.addToCartIframe();
    } else if (
      payload.data?.eventId === DATA_LAYER_EVENT &&
      payload.origin === window.location.origin &&
      payload.source === this.iframeComponent?.nativeElement?.contentWindow
    ) {
      // Re-push the EDDL payload forwarded from THIS iframe onto this (surviving) page's
      // adobeDataLayer, so it outlives the iframe teardown (DCP-61998). Guarded three ways:
      //  - origin === our own origin: never ingest a cross-origin payload;
      //  - source === this iframe's contentWindow: only accept messages from the iframe this
      //    component owns, so a sibling iframe / unrelated same-origin window cannot forge or
      //    duplicate analytics events.
      this.pushToOuterDataLayer(payload.data?.details);
    }
  };

  constructor(@Inject(PLATFORM_ID) public platformId: object) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('message', this.messageHandler, false);
    }
  }

  ngOnChanges(): void {
    if (this.show) {
      setTimeout(() => {
        this.iframeComponent?.nativeElement?.focus();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('message', this.messageHandler, false);
    }
  }

  closeIframe(): void {
    this.closeEvent.emit(false);
  }

  addToCartIframe(): void {
    setTimeout(() => {
      this.closeEvent.emit(true);
    }, 1000);
  }

  private pushToOuterDataLayer(data: EddlAdobeTrackingData): void {
    if (!isPlatformBrowser(this.platformId) || !data) {
      return;
    }
    // The Adobe data layer is an array augmented by Adobe Launch with a getState() accessor
    // (absent on a plain array before Launch initialises it).
    type AdobeDataLayer = EddlAdobeTrackingData[] & {getState?: () => {ecommerce?: {products?: unknown[]}}};
    const w = window as unknown as Record<string, AdobeDataLayer | undefined>;
    const layer: AdobeDataLayer = w[EDDL_DATA_LAYER_NAME] ?? ([] as unknown as AdobeDataLayer);
    w[EDDL_DATA_LAYER_NAME] = layer;
    // Apply the wipe buffer against THIS (outer) data layer's current product count - the forwarded
    // payload is un-wiped because the collector's buffer was sized for the iframe layer (DCP-61998).
    layer.push(withWipeBuffer(data, layer.getState?.()));
  }
}
